'use client';

import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { buildBazaLekowUrl } from '../lib/slug';

export interface CombinationDrug {
  id: string;
  productName: string;
  commonName: string;
  strength: string;
  form: string;
  hasChplPdf?: boolean;
  hasUlotkaPdf?: boolean;
  hasChplExtraction?: boolean;
  checkCount: number;
  matchedBySubstance: boolean;
}

interface CurrentDrug {
  id: string;
  productName: string;
  commonName: string;
  strength: string;
  form: string;
}

export interface TopCheckedCombinationsProps {
  drugId: string;
  drugName: string;
  drugStrength: string;
  drugCommonName: string;
  drugForm: string;
  initialDrugs?: CombinationDrug[];
  onSelectDrug?: (drug: CombinationDrug) => void;
  onHasData?: (hasData: boolean) => void;
}

function buildInteractionUrl(
  currentDrug: CurrentDrug,
  otherDrug: CombinationDrug,
) {
  return buildBazaLekowUrl([
    {
      id: currentDrug.id,
      product_name: currentDrug.productName,
      common_name: currentDrug.commonName,
      strength: currentDrug.strength,
      form: currentDrug.form,
    },
    {
      id: otherDrug.id,
      product_name: otherDrug.productName,
      common_name: otherDrug.commonName,
      strength: otherDrug.strength,
      form: otherDrug.form,
    },
  ]);
}

export function TopCheckedCombinations({
  drugId,
  drugName,
  drugStrength,
  drugCommonName,
  drugForm,
  initialDrugs,
  onSelectDrug,
  onHasData,
}: TopCheckedCombinationsProps) {
  const { t } = useTranslation('interactions');
  const [initialDrugId] = useState(initialDrugs ? drugId : null);
  const [fetchedDrugs, setFetchedDrugs] = useState<{
    drugId: string;
    drugs: CombinationDrug[] | null;
  } | null>(initialDrugs ? { drugId, drugs: initialDrugs } : null);
  const [hoveredSection, setHoveredSection] = useState<
    'direct' | 'substance' | null
  >(null);

  // Derive current drugs from fetched state — null when drugId doesn't match (loading)
  const drugs = fetchedDrugs?.drugId === drugId ? fetchedDrugs.drugs : null;

  useEffect(() => {
    // Skip fetch if we have server-provided data for this exact drug
    if (initialDrugs && drugId === initialDrugId) return;

    let cancelled = false;

    void fetch(
      `/api/interactions/top-combinations?drugId=${encodeURIComponent(drugId)}`,
    )
      .then((res) => res.json())
      .then((data: { drugs: CombinationDrug[] }) => {
        if (!cancelled) {
          setFetchedDrugs({
            drugId,
            drugs: data.drugs?.length > 0 ? data.drugs : null,
          });
        }
      })
      .catch(() => {
        // Silently fail — section just won't appear
      });

    return () => {
      cancelled = true;
    };
  }, [drugId, initialDrugs, initialDrugId]);

  const directDrugs = drugs?.filter((d) => !d.matchedBySubstance) ?? [];
  const _substanceDrugs = drugs?.filter((d) => d.matchedBySubstance) ?? [];

  // Only direct matches are rendered while the substance section is hidden
  const hasData = directDrugs.length > 0;

  useEffect(() => {
    onHasData?.(hasData);
  }, [hasData, onHasData]);

  if (!hasData) return null;

  const currentDrug: CurrentDrug = {
    id: drugId,
    productName: drugName,
    commonName: drugCommonName,
    strength: drugStrength,
    form: drugForm,
  };

  const renderDrugTags = (
    items: CombinationDrug[],
    header: React.ReactNode,
    section: 'direct' | 'substance',
  ) => (
    <div className="flex flex-wrap items-baseline gap-1.5">
      {header}
      {items.map((drug) => (
        <a
          key={drug.id}
          href={buildInteractionUrl(currentDrug, drug)}
          onMouseEnter={() => setHoveredSection(section)}
          onMouseLeave={() => setHoveredSection(null)}
          onClick={(e) => {
            if (onSelectDrug && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
              e.preventDefault();
              onSelectDrug(drug);
            }
          }}
          className={cn(
            'inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[11px] no-underline transition-all',
            drug.matchedBySubstance
              ? 'border-stone-200 bg-stone-50 text-stone-400 shadow-[1px_1px_0_0_#d6d3d1] hover:border-green-300 hover:bg-green-50 hover:text-green-700 hover:shadow-none'
              : 'border-stone-300 bg-white text-stone-500 shadow-[1px_1px_0_0_#d6d3d1] hover:border-green-300 hover:bg-green-50 hover:text-green-700 hover:shadow-none',
          )}
        >
          <span className="max-w-[160px] truncate">{drug.productName}</span>
          <span className="text-[9px] text-stone-400">{drug.strength}</span>
        </a>
      ))}
    </div>
  );

  return (
    <div className="space-y-4 rounded-lg bg-[#f7f5ef] p-4">
      <h2 className="font-heading -skew-x-3 text-xs font-semibold tracking-[0.08em] text-stone-500 uppercase">
        {t('topCombinations.title')}
      </h2>

      {directDrugs.length > 0 &&
        renderDrugTags(
          directDrugs,
          <>
            <h3
              className={cn(
                'inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium transition-all',
                hoveredSection === 'direct'
                  ? 'border-green-300 bg-green-50 text-green-700'
                  : 'border-transparent text-stone-500',
              )}
            >
              {drugName}
            </h3>
            <span className="text-sm font-semibold text-stone-400">+</span>
          </>,
          'direct',
        )}

      {/* Temporarily hidden: substance-matched drugs section
      {_substanceDrugs.length > 0 &&
        renderDrugTags(
          _substanceDrugs,
          <>
            <h3
              className={cn(
                'inline-flex items-center rounded border px-1.5 py-0.5 text-[11px] font-medium transition-all',
                hoveredSection === 'substance'
                  ? 'border-green-300 bg-green-50 text-green-700'
                  : 'border-transparent text-stone-400',
              )}
            >
              {drugCommonName}
            </h3>
            <span className="text-sm font-semibold text-stone-400">+</span>
          </>,
          'substance',
        )}
      */}
    </div>
  );
}
